import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useSport } from '../context/SportContext';
import HeaderStats from '../components/HeaderStats';
import FilterSection from '../components/FilterSection';
import EdgeRankedGames from '../components/EdgeRankedGames';
import NFLGameCard from '../components/NFLGameCard';
import { syncCurrentLine, NFL_LINE_KEYS } from '../utils/currentLine';
import { enrichPredictionsWithResults } from '../utils/api';
const NFLDashboard = () => {
    console.log('🏈 NFLDashboard component is rendering');
    const { currentSport } = useSport();
    const [predictions, setPredictions] = useState(null);
    const [filteredGames, setFilteredGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        team: '',
        confidence: 'ALL',
        conference: 'ALL',
        week: 'ALL',
        date: 'ALL'
    });
    useEffect(() => {
        const loadPredictions = async () => {
            try {
                setLoading(true);
                const timestamp = Date.now();
                const response = await fetch(`/static/nfl/nfl_predictions.json?v=${timestamp}`, {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                        'Expires': '0'
                    }
                });
                console.log('🏈 Fetching NFL predictions with timestamp:', timestamp);
                if (!response.ok) {
                    throw new Error('Failed to fetch NFL predictions');
                }
                const data = await response.json();
                console.log('🏈 Loaded predictions data:', data);
                console.log('🏈 Data has games:', !!data?.games);
                console.log('🏈 Data has predictions:', !!data?.predictions);
                console.log('🏈 Games count:', data?.games?.length);
                console.log('🏈 Predictions count:', data?.predictions?.length);
                // Transform the data structure to match what the components expect
                const rawGames = (data.predictions || data.games || []).map((game) => syncCurrentLine(game, NFL_LINE_KEYS));
                console.log('🏈 First raw game:', rawGames[0]);
                const games = rawGames.map((game) => {
                    // Extract nested data from new NFL schema
                    const pred = game.prediction || {};
                    const bettingIndicators = game.betting_indicators || {};
                    const bettingAnalysis = game.betting_analysis || {};
                    // Get picked team
                    const pickedTeam = pred.pick_team || game.home_team;
                    const isHomePick = pickedTeam === game.home_team;
                    // Get the correct spread for the picked team
                    // Fallback to game.spread_line if betting_analysis is null
                    const spreadLine = isHomePick ?
                        (bettingAnalysis.spread_home || game.spread_line || 0) :
                        (bettingAnalysis.spread_away || (game.spread_line ? -game.spread_line : 0));
                    // Calculate confidence percentage from pick_probability
                    const confidencePercentage = Math.round((pred.pick_probability || 0.5) * 100);
                    return {
                        id: game.game_id || `game_${Math.random()}`,
                        game_id: game.game_id || `game_${Math.random()}`,
                        home_team: game.home_team,
                        away_team: game.away_team,
                        game_date: game.game_date,
                        game_time: game.game_time,
                        venue: game.home_team, // venue field not in new structure
                        week: data.week || 1,
                        year: data.season || 2025,
                        // Prediction fields from nested structure
                        confidence_level: pred.confidence_tier || 'LOW',
                        confidence_percentage: confidencePercentage,
                        team_to_cover: pickedTeam,
                        spread_line: spreadLine,
                        ensemble_probability: pred.pick_probability || 0.5,
                        display_confidence: confidencePercentage,
                        home_covers: pred.home_covers,
                        pick_probability: pred.pick_probability,
                        // Betting indicators
                        betting_indicators: bettingIndicators,
                        betting_tier: bettingIndicators.betting_tier,
                        // Betting analysis fields
                        betting_analysis: bettingAnalysis,
                        home_spread_odds: bettingAnalysis.spread_home_odds || -110,
                        away_spread_odds: bettingAnalysis.spread_away_odds || -110,
                        spread_home: bettingAnalysis.spread_home,
                        spread_away: bettingAnalysis.spread_away,
                        // Edge context ranking (drives the default sort order)
                        edge_rank: game.edge_rank,
                        edge_context: game.edge_context,
                        // Store original data for reference
                        original_prediction_data: game
                    };
                });
                const transformedData = {
                    metadata: data.metadata || data,
                    games: games
                };
                console.log('🏈 Transformed games sample:', games[0]);
                console.log('🏈 Games with IDs:', games.map((g) => ({ id: g.id, game_id: g.game_id, teams: `${g.away_team} @ ${g.home_team}` })));
                // Enrich with game results
                console.log('🏈 Enriching with game results...');
                const enrichedData = await enrichPredictionsWithResults(transformedData, 'nfl');
                console.log('🏈 Enriched data:', enrichedData);
                console.log('🏈 First enriched game:', enrichedData.games[0]);
                console.log('🏈 First game has result?', !!enrichedData.games[0].result);
                console.log('🏈 First game result:', enrichedData.games[0].result);
                console.log('🏈 First game bet_outcome:', enrichedData.games[0].bet_outcome);
                setPredictions(enrichedData);
                setFilteredGames(enrichedData.games);
            }
            catch (err) {
                setError('Failed to load NFL predictions. Please try again later.');
                console.error('Error loading NFL predictions:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadPredictions();
    }, [currentSport]);
    useEffect(() => {
        if (!predictions)
            return;
        let filtered = predictions.games;
        // Apply team filter
        if (filters.team) {
            const teamLower = filters.team.toLowerCase();
            filtered = filtered.filter((game) => game.home_team.toLowerCase().includes(teamLower) ||
                game.away_team.toLowerCase().includes(teamLower));
        }
        // Apply confidence filter
        if (filters.confidence !== 'ALL') {
            filtered = filtered.filter((game) => game.confidence_level === filters.confidence);
        }
        // Apply week filter
        if (filters.week !== 'ALL') {
            filtered = filtered.filter((game) => game.week === parseInt(filters.week));
        }
        console.log('🏈 NFL filtered games:', filtered.length);
        if (filtered.length > 0) {
            console.log('🏈 First game structure:', filtered[0]);
            console.log('🏈 First filtered game has result?', !!filtered[0].result);
            console.log('🏈 First filtered game result:', filtered[0].result);
            console.log('🏈 Confidence levels in data:', [...new Set(filtered.map((g) => g.confidence_level))]);
            console.log('🏈 Has betting_indicators:', !!filtered[0].betting_indicators);
            console.log('🏈 betting_indicators structure:', filtered[0].betting_indicators);
        }
        setFilteredGames(filtered);
    }, [predictions, filters]);
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };
    if (loading) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "d-flex justify-content-center", children: _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsxs("div", { className: "alert alert-danger", role: "alert", children: [_jsx("i", { className: "fas fa-exclamation-triangle me-2" }), error] }) }));
    }
    if (!predictions) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "alert alert-info", role: "alert", children: "No NFL predictions available at this time." }) }));
    }
    return (_jsxs("div", { className: "container-mobile safe-area-padding", children: [_jsx(HeaderStats, { metadata: predictions?.metadata, games: predictions?.games || [], sport: currentSport }), _jsx(FilterSection, { filters: filters, onFilterChange: handleFilterChange, availableWeeks: predictions?.games ? [...new Set(predictions.games.map((g) => g.week))].filter(w => w != null).sort() : [] }), _jsx(EdgeRankedGames, { games: filteredGames, renderCard: (game) => _jsx(NFLGameCard, { game: game }) })] }));
};
export default NFLDashboard;
